import React from "react";

import { cn } from "@/lib/utils";
import type { StatusDotTone } from "./service-status.model";

type ToneMeta = { dot: string; pulse?: boolean };

// green = all good, amber = degraded, red = outage, blue = maintenance.
// Matches the connection-status vocabulary already used by the integrations
// hub (`integrations-hub/status-badge.tsx`) so one colour means one thing
// across the app.
const TONE_META: Record<Exclude<StatusDotTone, "none">, ToneMeta> = {
  green: { dot: "bg-green-500" },
  amber: { dot: "bg-amber-500", pulse: true },
  red: { dot: "bg-red-500", pulse: true },
  blue: { dot: "bg-blue-500" },
};

/**
 * Status dot. Incident tones radiate a dampened ping ring behind a solid core;
 * the ring is `motion-safe` only, so a reduced-motion user gets a static dot.
 *
 * Renders nothing for the `none` tone — the fail-silent state where we cannot
 * verify status and must not imply one.
 */
export function ServiceStatusDot({
  tone,
  className,
}: {
  tone: StatusDotTone;
  className?: string;
}): React.ReactElement | null {
  if (tone === "none") return null;

  const meta = TONE_META[tone];

  if (meta.pulse) {
    return (
      <span
        className={cn("relative inline-flex h-2.5 w-2.5 shrink-0", className)}
        data-testid="service-status-dot"
        data-tone={tone}
        aria-hidden="true"
      >
        <span
          className={cn(
            "absolute inline-flex h-full w-full rounded-full opacity-60 motion-safe:animate-ping",
            meta.dot
          )}
        />
        <span
          className={cn(
            "relative inline-flex h-full w-full rounded-full",
            meta.dot
          )}
        />
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-block h-2.5 w-2.5 shrink-0 rounded-full",
        meta.dot,
        className
      )}
      data-testid="service-status-dot"
      data-tone={tone}
      aria-hidden="true"
    />
  );
}
